import { useEffect, useMemo, useState } from "react";
import { optimizeUrl } from "@/lib/img";
import { useSearchParams, useLocation, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import AppShell from "@/components/AppShell";
import SectionTabs from "@/components/SectionTabs";
import { useAuth } from "@/context/AuthContext";
import { EVENT_TYPES, eventTypeMeta, normalizeEventType } from "@/lib/roleCategories";
import {
  Calendar, MapPin, Clock, Images, CalendarDays, Trophy, Users, Heart, Check, Search,
  Sparkles, ArrowUpRight, Camera, Shirt, Store, GraduationCap, Handshake, Wine, Ticket, X,
} from "lucide-react";

const TYPE_ICON = { casting: Ticket, shooting: Camera, defile: Shirt, showroom: Store, masterclass: GraduationCap, networking: Handshake, soiree: Wine };

function eventCover(e) {
  if (e.cover_image) return optimizeUrl(`${API}/files/${e.cover_image}`, 1024);
  if (e.image_url) return optimizeUrl(e.image_url, 1024);
  return null;
}
const monthKey = (d) => (d || "").slice(0, 7);
const monthLabel = (key) => {
  const [y, m] = key.split("-").map(Number);
  const s = new Date(y, (m || 1) - 1, 1).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const dayNum = (d) => (d ? new Date(d).getDate() : "");
const monthShort = (d) => (d ? new Date(d).toLocaleDateString("fr-FR", { month: "short" }).replace(".", "") : "");

export default function Events() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const isCastingRoute = location.pathname.endsWith("/castings");
  const type = isCastingRoute ? "casting" : (params.get("type") || "");

  useEffect(() => {
    setLoading(true);
    api.get("/events", { params: { status: "upcoming" } })
      .then(({ data }) => setEvents(Array.isArray(data) ? data : []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  const counts = useMemo(() => {
    const c = {};
    events.forEach((e) => { const t = normalizeEventType(e.event_type); c[t] = (c[t] || 0) + 1; });
    return c;
  }, [events]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return events.filter((e) => {
      if (type && normalizeEventType(e.event_type) !== type) return false;
      if (needle) return [e.title, e.description, e.location].some((s) => (s || "").toLowerCase().includes(needle));
      return true;
    });
  }, [events, type, q]);

  const featured = filtered[0] || null;
  const grouped = useMemo(() => {
    const rest = filtered.slice(featured ? 1 : 0);
    const map = new Map();
    rest.forEach((e) => { const k = monthKey(e.date); if (!map.has(k)) map.set(k, []); map.get(k).push(e); });
    return [...map.entries()];
  }, [filtered, featured]);

  const setType = (v) => {
    if (isCastingRoute) return;
    const next = new URLSearchParams(params);
    if (v) next.set("type", v); else next.delete("type");
    setParams(next, { replace: true });
  };

  const showTabs = user?.role === "talent";

  return (
    <AppShell>
      <div data-testid="events-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        {showTabs ? (
          <SectionTabs
            eyebrow="L'agenda mode"
            title={isCastingRoute ? "Castings" : "Événements"}
            subtitle="Castings, shootings, défilés, showrooms, masterclass et soirées : tout l'agenda Helencia."
            tabs={[
              { id: "events", to: "/events", label: "Événements", icon: CalendarDays, match: (p) => p === "/events" },
              { id: "concours", to: "/talent/concours", label: "Concours", icon: Trophy },
              { id: "my-entries", to: "/talent/my-contest-entries", label: "Mes participations", icon: Heart },
              { id: "my-events", to: "/talent/my-events", label: "Mes événements", icon: Calendar },
              { id: "castings", to: "/talent/castings", label: "Castings", icon: Users },
            ]}
            testid="events-tabs"
          />
        ) : (
          <div className="mb-8">
            <p className="caption-label text-[#A26F3E] mb-2">L'agenda mode</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] leading-tight">
              {isCastingRoute ? "Castings à venir" : "Événements à venir"}
            </h1>
            <p className="text-sm text-[#8B8680] mt-2 max-w-2xl">Castings, shootings, défilés, showrooms, masterclass et soirées : tout l'agenda Helencia.</p>
          </div>
        )}

        {/* Search + type filters */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-10" data-testid="events-filters">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8B8680]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher un événement, une ville…"
              className="w-full h-12 pl-11 pr-10 rounded-full bg-white border border-[#EADDC9] text-sm focus:outline-none focus:border-[#C68E59] transition"
              data-testid="events-search"
            />
            {q && <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-[#FAF3EA] flex items-center justify-center" data-testid="events-search-clear"><X className="w-3.5 h-3.5 text-[#8B8680]" /></button>}
          </div>
          {!isCastingRoute && (
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 lg:mx-0 lg:px-0 lg:flex-wrap" data-testid="events-type-filters">
              <TypeChip active={!type} label="Tous" count={events.length} onClick={() => setType("")} testid="type-chip-all" />
              {EVENT_TYPES.map((t) => (
                <TypeChip key={t.value} active={type === t.value} label={t.short} icon={TYPE_ICON[t.value]} count={counts[t.value] || 0} onClick={() => setType(t.value)} testid={`type-chip-${t.value}`} />
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24"><div className="w-10 h-10 border-2 border-[#C68E59] border-t-transparent rounded-full animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="rounded-3xl bg-white border border-dashed border-[#EADDC9] p-14 text-center" data-testid="events-empty">
            <CalendarDays className="w-10 h-10 text-[#C68E59] mx-auto opacity-60" />
            <p className="font-serif text-xl text-[#1A1A1A] mt-4">Aucun événement {type ? `de type « ${eventTypeMeta(type).label} »` : ""} pour le moment</p>
            <p className="text-sm text-[#8B8680] mt-2">De nouveaux castings et rendez-vous sont ajoutés chaque semaine.</p>
          </div>
        ) : (
          <>
            {featured && <FeaturedEvent e={featured} />}
            {grouped.map(([k, list]) => (
              <section key={k} className="mb-12" data-testid={`events-month-${k}`}>
                <div className="flex items-center gap-4 mb-5">
                  <h2 className="font-serif text-2xl sm:text-3xl text-[#1A1A1A]">{monthLabel(k)}</h2>
                  <span className="text-xs text-[#8B8680]">{list.length} événement{list.length > 1 ? "s" : ""}</span>
                  <div className="flex-1 h-px bg-[#EADDC9]" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {list.map((e) => <EventCard key={e.id} e={e} />)}
                </div>
              </section>
            ))}
          </>
        )}
      </div>
    </AppShell>
  );
}

function TypeChip({ active, label, icon: Icon, count, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className={`shrink-0 inline-flex items-center gap-2 h-10 px-4 rounded-full text-xs font-medium border transition ${
        active ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "bg-white text-[#6E4B2A] border-[#EADDC9] hover:border-[#C68E59]"
      }`}
      data-testid={testid}
    >
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {label}
      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${active ? "bg-white/15" : "bg-[#FAF3EA]"}`}>{count}</span>
    </button>
  );
}

function FeaturedEvent({ e }) {
  const meta = eventTypeMeta(e.event_type);
  const Icon = TYPE_ICON[meta.value] || Sparkles;
  const cover = eventCover(e);
  return (
    <Link to={`/events/${e.id}`} className="group block mb-14 rounded-[2rem] overflow-hidden bg-[#1A1A1A] text-white relative shadow-[0_30px_80px_-40px_rgba(0,0,0,0.6)]" data-testid={`event-featured-${e.id}`}>
      <div className="grid lg:grid-cols-5">
        <div className="lg:col-span-3 relative aspect-[16/10] lg:aspect-auto lg:min-h-[420px] overflow-hidden bg-[#2a2a2a]">
          {cover && <img src={cover} alt={e.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition duration-1000" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-[#1A1A1A]" />
          <div className="absolute top-5 left-5 flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur text-[10px] uppercase tracking-[0.16em] text-[#1A1A1A] font-semibold"><Icon className="w-3 h-3" /> {meta.label}</span>
            <span className="px-3 py-1.5 rounded-full bg-[#C68E59] text-[10px] uppercase tracking-[0.16em] text-white font-semibold">Prochain rendez-vous</span>
          </div>
          <div className="absolute bottom-5 left-5 rounded-2xl bg-white text-[#1A1A1A] px-4 py-3 text-center shadow-lg">
            <div className="font-serif text-3xl leading-none">{dayNum(e.date)}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] mt-1">{monthShort(e.date)}</div>
          </div>
        </div>
        <div className="lg:col-span-2 p-8 sm:p-10 lg:p-12 flex flex-col justify-center relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#C68E59]/25 blur-[90px] pointer-events-none" />
          <p className="caption-label text-[#E6C29A]">À ne pas manquer</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-3 leading-[1.05]">{e.title}</h2>
          <p className="text-white/70 text-sm mt-4 leading-relaxed line-clamp-3">{e.description}</p>
          <div className="mt-6 space-y-2 text-sm text-white/85">
            <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#E6C29A]" />{e.date && new Date(e.date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
            {e.time && <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#E6C29A]" />{e.time}</div>}
            <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#E6C29A]" />{e.location}</div>
          </div>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#C68E59] group-hover:bg-[#A26F3E] text-white text-xs font-semibold px-6 h-11 transition">
              {e.has_rsvped ? "Voir mon inscription" : "Découvrir & s'inscrire"} <ArrowUpRight className="w-4 h-4" />
            </span>
            <span className="text-xs text-white/60 inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {e.participants_count ?? 0} inscrit{(e.participants_count ?? 0) > 1 ? "s" : ""}{e.spots_left != null && ` · ${e.spots_left} place${e.spots_left > 1 ? "s" : ""} restante${e.spots_left > 1 ? "s" : ""}`}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function EventCard({ e }) {
  const meta = eventTypeMeta(e.event_type);
  const Icon = TYPE_ICON[meta.value] || Sparkles;
  const cover = eventCover(e);
  const extras = (e.gallery_images?.length || 0) + (e.event_gallery?.length || 0) + (e.poster_image ? 1 : 0);
  return (
    <Link to={`/events/${e.id}`} className="card-lux overflow-hidden flex flex-col group h-full" data-testid={`event-${e.id}`}>
      <div className="aspect-[16/10] overflow-hidden relative bg-[#FAF3EA]">
        {cover ? (
          <img src={cover} alt={e.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Icon className="w-10 h-10 text-[#C68E59]/50" /></div>
        )}
        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/90 backdrop-blur text-[10px] tracking-widest uppercase text-[#1A1A1A] font-semibold">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.accent }} /> {meta.short}
        </span>
        {e.has_rsvped && (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#2E7D32] text-white text-[10px] font-semibold uppercase tracking-wider"><Check className="w-3 h-3" /> Inscrit</span>
        )}
        <div className="absolute bottom-3 left-3 rounded-xl bg-white text-[#1A1A1A] px-3 py-2 text-center shadow">
          <div className="font-serif text-xl leading-none">{dayNum(e.date)}</div>
          <div className="text-[9px] uppercase tracking-[0.18em] mt-0.5">{monthShort(e.date)}</div>
        </div>
        {extras > 0 && (
          <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/70 backdrop-blur text-white text-[10px]"><Images className="w-3 h-3" /> +{extras}</span>
        )}
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <h3 className="font-serif text-xl sm:text-2xl text-[#1A1A1A] leading-tight">{e.title}</h3>
        <p className="text-sm text-[#666] leading-relaxed mt-2 line-clamp-2 flex-1">{e.description}</p>
        <div className="mt-4 space-y-1.5 text-xs text-[#6E4B2A]">
          {e.time && <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5" />{e.time}</div>}
          <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" />{e.location}</div>
        </div>
        <div className="mt-4 pt-4 border-t border-[#FAF3EA] flex items-center justify-between text-xs">
          <span className="inline-flex items-center gap-1.5 text-[#8B8680]"><Users className="w-3.5 h-3.5" /> {e.participants_count ?? 0} inscrit{(e.participants_count ?? 0) > 1 ? "s" : ""}</span>
          {e.spots_left != null ? (
            <span className={`font-semibold ${e.spots_left <= 3 ? "text-[#A3483F]" : "text-[#2E7D32]"}`}>{e.spots_left > 0 ? `${e.spots_left} place${e.spots_left > 1 ? "s" : ""}` : "Liste d'attente"}</span>
          ) : (
            <span className="text-[#C68E59] font-semibold inline-flex items-center gap-1 group-hover:translate-x-0.5 transition">Voir <ArrowUpRight className="w-3.5 h-3.5" /></span>
          )}
        </div>
      </div>
    </Link>
  );
}
